import { useState, useEffect, useCallback } from 'react';
import { API } from '@/utils/API';
import { Document } from '@/hooks/submissions/useSubmissionDocuments';

interface StudyDocumentsResponse {
  study_id: string;
  documents: Document[];
}

interface UseStudyDocumentsReturn {
  documents: Document[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearError: () => void;
}

export const useStudyDocuments = (
  studyId: string | null
): UseStudyDocumentsReturn => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!studyId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch(
        `/studies/${studyId}/documents`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data: StudyDocumentsResponse = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [studyId]);

  useEffect(() => {
    if (studyId) {
      fetchDocuments();
    }
  }, [studyId, fetchDocuments]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    documents,
    isLoading,
    error,
    refetch: fetchDocuments,
    clearError,
  };
};
